import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { DataGap, EvidenceItem, ImportResult, ObservationsSummary } from '../types';
import { DataSourcesPanel } from '../components/DataSourcesPanel';

interface Props {
  onDataChange?: () => void;
}

export function EvidencePage({ onDataChange }: Props) {
  const [elasticities, setElasticities] = useState<EvidenceItem[]>([]);
  const [gaps, setGaps] = useState<DataGap[]>([]);
  const [summary, setSummary] = useState<ObservationsSummary | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    Promise.all([api.elasticities(), api.dataGaps(), api.observationsSummary()]).then(([e, g, s]) => {
      setElasticities(e);
      setGaps(g);
      setSummary(s);
    }).catch(console.error);
  };

  useEffect(() => { load(); }, []);

  const handleImport = async (file: File) => {
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await api.importObservations(file);
      setImportResult(result);
      load();
      onDataChange?.();
    } catch (e) {
      setImportError(String(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Data & Evidence</h1>
      <p className="page-subtitle">Import official observations, review elasticity evidence, and track data gaps.</p>

      <DataSourcesPanel onDataChange={() => { load(); onDataChange?.(); }} />

      <div className="card">
        <h3>Official Data Import (CSV / XLSX — PBS, Finance Division, FBR)</h3>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0 }}>
          Upload CSV or XLSX matching the official import template. Imported values refresh vetting and the status banner.
        </p>
        {summary && (
          <p style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
            {summary.total_observations} observations across {summary.distinct_variables} variables
            {summary.last_imported_at ? ` · Last import: ${summary.last_imported_at.slice(0, 19)}` : ''}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ fontSize: '0.85rem' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImport(f);
            }}
          />
          <a className="btn secondary" href="/official_data_import_template.csv" download style={{ display: 'inline-block', padding: '0.5rem 1rem', fontSize: '0.875rem' }}>
            Download template
          </a>
        </div>
        {importing && <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Importing…</p>}
        {importResult && (
          <div className="import-result">
            Imported: {importResult.imported} · Skipped: {importResult.skipped}
            {importResult.errors.length > 0 && (
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.8rem' }}>
                {importResult.errors.slice(0, 5).map((err) => <li key={err}>{err}</li>)}
              </ul>
            )}
          </div>
        )}
        {importError && <p className="error" style={{ textAlign: 'left', padding: '0.5rem 0' }}>{importError}</p>}
      </div>

      <div className="card">
        <h3>Elasticity / Parameter Evidence</h3>
        <table className="data-table">
          <thead>
            <tr><th>ID</th><th>Parameter</th><th>Estimate</th><th>Geography</th><th>Reference</th><th>Confidence</th></tr>
          </thead>
          <tbody>
            {elasticities.map((e) => (
              <tr key={e.evidence_id}>
                <td>{e.evidence_id}</td>
                <td>{e.linkage_name}</td>
                <td>{e.central_estimate}</td>
                <td>{e.geography}</td>
                <td>{e.reference}</td>
                <td>{e.confidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Data Gaps ({gaps.length})</h3>
        <table className="data-table">
          <thead>
            <tr><th>Variable</th><th>Indicator</th><th>Classification</th><th>Organisation</th><th>Priority</th></tr>
          </thead>
          <tbody>
            {gaps.slice(0, 30).map((g, i) => (
              <tr key={`${g.variable_id}-${i}`}>
                <td>{g.variable_id}</td>
                <td>{g.indicator}</td>
                <td>{g.gap_classification}</td>
                <td>{g.responsible_org}</td>
                <td>{g.priority}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
